import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  LayoutDashboard,
  FileText,
  Users,
  BarChart,
  Settings,
  User,
  Menu,
  X,
  ChevronDown,
  ChevronRight,
  Mail,
  Shield,
  UserCircle,
  GraduationCap,
  TrendingUp,
  Download,
  RefreshCw,
  List,
  FileType,
  Mail as MailIcon,
  UserCheck,
  FolderOpen,
  Bell,
  Megaphone,
  CheckCircle,
  XCircle,
  Clock,
  Sliders,
} from 'lucide-react';

const ADMIN_NAV = [
  {
    section: 'Dashboard',
    items: [
      { name: 'Dashboard', path: '/admin/dashboard', icon: LayoutDashboard }
    ]
  },
  {
    section: 'Applications',
    items: [
      { name: 'All Applications', path: '/admin/applications', icon: FileText },
      { name: 'Pending Review', path: '/admin/applications?status=UNDER_REVIEW', icon: Clock },
      { name: 'Recent Submissions', path: '/admin/applications?recent=true', icon: TrendingUp }
    ]
  },
  {
    section: 'User Management',
    items: [
      { name: 'All Users', path: '/admin/users', icon: Users },
      { name: 'Admins', path: '/admin/users?role=ADMIN', icon: Shield },
      { name: 'Educators', path: '/admin/users?role=EDUCATOR', icon: GraduationCap },
      { name: 'Students', path: '/admin/users?role=STUDENT', icon: UserCircle }
    ]
  },
  {
    section: 'Documents',
    items: [
      { name: 'All Documents', path: '/admin/documents', icon: FolderOpen },
      { name: 'Pending Review', path: '/admin/documents?status=pending', icon: Clock },
      { name: 'Approved', path: '/admin/documents?status=approved', icon: CheckCircle },
      { name: 'Rejected', path: '/admin/documents?status=rejected', icon: XCircle }
    ]
  },
  {
    section: 'Reports & Analytics',
    items: [
      { name: 'Overview', path: '/admin/reports', icon: BarChart },
      { name: 'Feature Analytics', path: '/admin/reports/features', icon: TrendingUp },
      { name: 'User Analytics', path: '/admin/reports/users', icon: UserCheck },
      { name: 'Export Data', path: '/admin/reports/export', icon: Download }
    ]
  },
  {
    section: 'Communications',
    items: [
      { name: 'Send Notifications', path: '/admin/communications/notifications', icon: Bell },
      { name: 'Announcements', path: '/admin/communications/announcements', icon: Megaphone }
    ]
  },
  {
    section: 'Settings',
    items: [
      { name: 'Platform Settings', path: '/admin/settings/system', icon: Settings },
      { name: 'Feature Management', path: '/admin/settings/features', icon: Sliders },
      { name: 'Application Workflows', path: '/admin/settings/statuses', icon: List },
      { name: 'Document Types', path: '/admin/settings/documents', icon: FileType },
      { name: 'Email Templates', path: '/admin/settings/emails', icon: MailIcon }
    ]
  }
];

function NavSection({ section, items, collapsible, defaultOpen }) {
  const [isOpen, setIsOpen] = useState(defaultOpen !== false);
  const currentPath = window.location.pathname;

  return (
    <div className="mb-2">
      {/* Section Header */}
      <button
        onClick={() => collapsible && setIsOpen(!isOpen)}
        className={`w-full flex items-center justify-between px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider ${
          collapsible ? 'hover:bg-gray-50 cursor-pointer' : ''
        }`}
      >
        <span>{section}</span>
        {collapsible && (
          isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />
        )}
      </button>

      {/* Section Items */}
      {isOpen && (
        <div className="space-y-0.5 mt-1">
          {items.map((item) => {
            const Icon = item.icon;
            const isActive = currentPath === item.path || currentPath.startsWith(item.path + '/');

            return (
              <Link
                key={item.path}
                to={item.comingSoon ? '#' : item.path}
                onClick={(e) => item.comingSoon && e.preventDefault()}
                className={`flex items-center justify-between space-x-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-gray-100 text-gray-900 border-l-4 border-yellow-500'
                    : item.comingSoon
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <Icon className="w-4 h-4 flex-shrink-0" />
                  <span className="truncate">{item.name}</span>
                </div>
                {item.comingSoon && (
                  <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full">Soon</span>
                )}
                {item.active && !isActive && (
                  <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                )}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function AdminLayout({ children, title }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-gray-200 transform transition-transform duration-300 lg:translate-x-0 ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <div className="h-full flex flex-col">
          {/* Logo - Fixed */}
          <div className="h-16 flex items-center justify-between px-6 border-b border-gray-200 flex-shrink-0">
            <Link to="/" className="flex items-center space-x-2">
              <div className="w-8 h-8 rounded-lg bg-yellow-500 flex items-center justify-center">
                <span className="text-white font-bold text-sm">E</span>
              </div>
              <span className="font-bold text-gray-900">Educential</span>
            </Link>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden text-gray-500 hover:text-gray-700"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Navigation - Scrollable */}
          <nav className="flex-1 overflow-y-auto min-h-0">
            <div className="px-3 py-4">
              {ADMIN_NAV.map((navGroup, index) => (
                <NavSection
                  key={index}
                  section={navGroup.section}
                  items={navGroup.items}
                  collapsible={navGroup.collapsible}
                  defaultOpen={navGroup.defaultOpen}
                />
              ))}
            </div>
          </nav>

          {/* Bottom Section - Fixed (Help & Profile) */}
          <div className="border-t border-gray-200 flex-shrink-0">
            {/* Help Section */}
            <div className="px-3 py-3">
              <a
                href="mailto:info.educential@gmail.com"
                className="flex items-center space-x-3 px-3 py-2 rounded-lg text-gray-700 hover:bg-gray-50 text-sm font-medium transition-colors"
              >
                <Mail className="w-4 h-4 flex-shrink-0" />
                <span>Get Help</span>
              </a>
            </div>

            {/* Profile Section */}
            <div className="px-3 pb-4 pt-2 border-t border-gray-200">
              <div className="relative group">
                <button className="w-full flex items-center space-x-3 px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors">
                  <div className="w-9 h-9 rounded-full bg-yellow-100 flex items-center justify-center flex-shrink-0">
                    <User className="w-5 h-5 text-yellow-600" />
                  </div>
                  <div className="flex-1 text-left min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {user?.fullName || 'Admin'}
                    </p>
                    <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                  </div>
                  <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
                </button>

                {/* Dropdown Menu */}
                <div className="absolute bottom-full left-0 right-0 mb-2 bg-white border border-gray-200 rounded-lg shadow-lg py-1 hidden group-hover:block">
                  <Link
                    to="/profile"
                    className="flex items-center space-x-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <User className="w-4 h-4 text-gray-500" />
                    <span>Profile</span>
                  </Link>
                  <Link
                    to="/admin/settings/system"
                    className="flex items-center space-x-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <Settings className="w-4 h-4 text-gray-500" />
                    <span>Preferences</span>
                  </Link>
                  <div className="border-t border-gray-200 my-1"></div>
                  <button
                    onClick={logout}
                    className="w-full flex items-center space-x-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                  >
                    <span className="w-4 h-4 flex items-center justify-center">→</span>
                    <span>Logout</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </aside>

      {/* Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen lg:ml-64">
        {/* Top Navbar */}
        <header className="h-16 bg-white border-b border-gray-200 sticky top-0 z-30">
          <div className="h-full flex items-center justify-between px-4 lg:px-6">
            {/* Left Side */}
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden text-gray-500 hover:text-gray-700 p-2"
              >
                <Menu className="w-6 h-6" />
              </button>
              <h1 className="text-lg font-bold text-gray-900">{title || 'Admin Dashboard'}</h1>
            </div>

            {/* Right Side */}
            <div className="flex items-center space-x-3">
              <div className="px-3 py-1.5 bg-yellow-100 text-yellow-800 text-xs font-medium rounded-full">
                Admin
              </div>
            </div>
          </div>
        </header>

        {/* Main Content Area */}
        <main className="flex-1 overflow-y-auto">
          {children}
        </main>
      </div>
    </div>
  );
}
